import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FaCar, FaCheck, FaDownload, FaEdit, FaLock, FaPlus, FaSearch, FaTimes, FaTrashAlt } from 'react-icons/fa';
import {
  getAllCars, deleteCar, deactivateCar, exportCars,
  setCurrentPage, setPageSize, setCurrentParams, setActiveType,
} from 'redux/booking/car/carSlice';
import { debounce } from 'helpers/debounce';
import { Card, AgGridTable, Pagination, Button, Confirmation } from 'components/common';
import CarForm from 'components/app/bookings/car/car/CarForm';

const ActiveCell = ({ value }) => (value ? <FaCheck className="tw-text-green-600" /> : <FaTimes className="tw-text-slate-300" />);
ActiveCell.propTypes = { value: PropTypes.bool };

const CONDITIONS = [{ id: 'all', label: 'All' }, { id: 'active', label: 'Active' }, { id: 'disabled', label: 'Disabled' }];

const CarSettingsPage = () => {
  const dispatch = useDispatch();
  const { data = [], totalRows = 0, totalPagesCount = 1, pageSize = 100, activeType = 'active', currentParams = {}, loading, exporting } = useSelector((s) => s.booking_setting_car);

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formTitle, setFormTitle] = useState('New Car');
  const [showDelete, setShowDelete] = useState(false);
  const [page, setPage] = useState(currentParams.page || 1);

  const searchRef = useRef('');
  const car = selected.at(-1);

  const columnDefs = useMemo(() => [
    { headerName: 'ID#', field: 'id', minWidth: 90 },
    { headerName: 'Name', field: 'name', minWidth: 180 },
    { headerName: 'Model', field: 'model', minWidth: 160 },
    { headerName: 'Site', field: 'site_name', minWidth: 160 },
    { headerName: 'Active', field: 'active', minWidth: 100, cellRenderer: ActiveCell },
    { headerName: 'Note', field: 'remark', minWidth: 180 },
    { headerName: 'Created by', field: 'created_by', minWidth: 140 },
    { headerName: 'Created date', field: 'created_at', minWidth: 160 },
    { headerName: 'Updated date', field: 'updated_at', minWidth: 160 },
  ], []);

  const runFetch = useCallback((pageNum, active) => {
    const params = { q: searchRef.current, active: active ?? activeType, page: pageNum, per_page: pageSize };
    setPage(pageNum);
    dispatch(setCurrentPage(pageNum));
    dispatch(setCurrentParams(params));
    dispatch(getAllCars(params));
  }, [dispatch, activeType, pageSize]);

  const didInit = useRef(false);
  useEffect(() => {
    if (!didInit.current) { didInit.current = true; dispatch(getAllCars({ active: activeType, page: 1, per_page: pageSize })); }
  }, [dispatch, activeType, pageSize]);

  const debouncedSearch = useRef(debounce(() => runFetch(1), 500)).current;
  const handleSearch = (e) => { const v = e.target.value; setSearch(v); searchRef.current = v; debouncedSearch(); };
  const handleClearSearch = () => { setSearch(''); searchRef.current = ''; runFetch(1); };
  const handleCondition = (id) => { dispatch(setActiveType(id)); runFetch(1, id); };
  const handlePageChange = (p) => runFetch(p);
  const handlePageSizeChange = (sz) => { dispatch(setPageSize(sz)); dispatch(getAllCars({ ...currentParams, per_page: sz, page: 1 })); };
  const handleExport = () => dispatch(exportCars(currentParams));

  const requireOne = () => {
    if (selected.length === 0) { toast.error('Car has not selected yet.'); return false; }
    if (selected.length > 1) { toast.error('You are selecting multiple records. Please select just one car.'); return false; }
    return true;
  };
  const permBlocked = (flag, msg) => { if (car?.permissions && car.permissions[flag] === false) { toast.error(msg); return true; } return false; };

  const handleNew = () => { setFormTitle('New Car'); setShowForm(true); };
  const handleEdit = () => { if (!requireOne()) return; if (permBlocked('allow_edit', "You don't have permission to edit this car.")) return; setFormTitle('Edit Car'); setShowForm(true); };
  const handleDeactivate = () => { if (!requireOne()) return; if (permBlocked('allow_deactivate', "You don't have permission to change this car.")) return; dispatch(deactivateCar(car)); };
  const handleDelete = () => { if (!requireOne()) return; if (permBlocked('allow_delete', "You don't have permission to delete this car.")) return; setShowDelete(true); };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
        <div className="tw-flex tw-items-center tw-gap-2">
          <FaCar className="tw-text-blue-600" size={20} />
          <div>
            <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Cars</h1>
            <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Manage the fleet of company cars available for booking.</p>
          </div>
        </div>
      </div>

      <Card padding="none">
        <div className="tw-p-4">
          <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-mb-3">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
              <div className="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-text-slate-600">
                <span className="tw-font-medium">Condition:</span>
                {CONDITIONS.map((c) => (
                  <label key={c.id} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-cursor-pointer">
                    <input type="radio" name="car-condition" checked={activeType === c.id} onChange={() => handleCondition(c.id)} />
                    <span>{c.label}</span>
                  </label>
                ))}
              </div>

              <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

              <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-56 sm:tw-flex-shrink-0">
                <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
                <input type="text" value={search} onChange={handleSearch} placeholder="Search cars..."
                  className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-solid tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700" />
                {search && (
                  <button type="button" onClick={handleClearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-p-0">
                    <FaTimes size={8} />
                  </button>
                )}
              </div>

              <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
              <Button variant="primary" size="sm" leftIcon={FaPlus} onClick={handleNew}>New</Button>
              <Button variant="secondary" size="sm" leftIcon={FaEdit} onClick={handleEdit}>Edit</Button>
              <Button variant="secondary" size="sm" leftIcon={FaLock} onClick={handleDeactivate}>De-active</Button>
              <Button variant="danger" size="sm" leftIcon={FaTrashAlt} onClick={handleDelete}>Delete</Button>
              <Button variant="secondary" size="sm" leftIcon={FaDownload} loading={exporting} onClick={handleExport}>Export</Button>

              <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
                {(totalRows || 0).toLocaleString()} records{selected.length ? ` • ${selected.length} selected` : ''}
              </span>
            </div>
          </div>

          <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden">
            <AgGridTable rowData={data} columnDefs={columnDefs} pagination={false} filter={false} multiSelect height="60vh" loading={loading} onSelectionChanged={setSelected} />
            <Pagination currentPage={page} totalPages={totalPagesCount} totalResults={totalRows} perPage={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} pageSizeOptions={[50, 100, 200, 500]} />
          </div>
        </div>
      </Card>

      {showForm && <CarForm showModal={showForm} setshowModal={setShowForm} title={formTitle} car={formTitle === 'New Car' ? null : car} />}

      <Confirmation
        show={showDelete}
        type="danger"
        title="Delete car"
        message="Are you sure to delete?"
        confirmText="Delete"
        onConfirm={() => { dispatch(deleteCar(car)); setShowDelete(false); }}
        onCancel={() => setShowDelete(false)}
      />
    </div>
  );
};

export default CarSettingsPage;
